import React, { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import memoize from 'lodash/memoize';
import Checkbox from '@atlaskit/checkbox';
import Tooltip from '@atlaskit/tooltip';
import defaultTo from 'lodash/defaultTo';
import { Text } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { ExpandableRow } from '@css-xp/base';
import { CartLineItemsTable, getCloudSiteOrgRow } from '@css-xp/ppc-purchase-experience';
import {
  LinkedEntitlements,
  PresentationLayerProducts,
  Product,
  ProductIdPlanOptionsMap,
  ProductLineItems,
  StandaloneProductWithApps,
  cartLinkedEntitlement,
} from '../types';
import { CartLineItemRowV2 } from './cart-line-item-row-v2';
import { CartProductSchema } from '../../../../types';
import { NEW_CLOUD_SITE_ID, NEW_CLOUD_SITE } from '../../../../constants';
import { SelectType } from '../../../billing-profile/src/types';
import { State } from '../../../../store/types';
import { CatalogueContextItems } from '../../../add-new-products/src/types';

export const CART_COLUMNS_CONSTANTS = {
  SELECT: 'Select',
  PRODUCT: 'Product',
  SUBSCRIPTION: 'Subscription',
  PLAN: 'Plan',
  CUSTOMER_CONTACT: 'Customer contact',
  LIST_PRICE: 'List price',
  PARTNER_DISCOUNT: 'Partner discount',
  OTHER_DISCOUNT: 'Other discounts',
  NEXT_BILL_DATE: 'Next bill date',
  QUANTITY: 'Quantity',
  BILLING_CYCLE: 'Billing cycle',
  STATUS: 'Status',
  TOTAL: 'Total',
  TAX_ESTIMATE: 'Tax estimate',
};

type Props = {
  productLineItems: ProductLineItems;
  productIdPlanOptionsMap: ProductIdPlanOptionsMap;
  selectedBillDate?: string;
  checkedBillDate?: Record<string, string>;
  setSelectedPricingPlanId: (
    updatedPricingPlanId: string,
    updatedOfferingId: string,
    productId: CartProductSchema['id']
  ) => void;
  setActiveUsers: (plan: string, productId: CartProductSchema['id']) => void;
  onRemoveProductFromCart: (productId: CartProductSchema['id']) => void;
  handleCheckedProductLineItems: (productId: string) => void;
  checkedItemMap: Record<string, SelectType>;
  selectiveProductRemovalEnable: boolean;
  matchBillDateEnabled?: boolean;
  isStickyColumnEnabled?: boolean;
  showProductsPromotionView?: boolean;
  showTotalColumn?: boolean;
  showTaxEstimationColumn?: boolean;
  shouldShowTrialStatus?: boolean;
  enrichmentStatus?: State['enrichmentStatus'];
  isAllowedToEditUsersCount?: boolean;
  showUnchangedLineItems?: boolean;
  selectedProductIdsWithoutChanges?: string[];
  hideCustomerContactColumn?: boolean;
  showSandboxProducts?: boolean;
  isSandboxDecouplingEnabled?: boolean;
  isNonCommercialPricingEnabled?: boolean;
  nextBillDate?: State['nextBillDate'];
  handleCheckedSelectAll: () => void;
  isIndeterminate: boolean;
  isSelectAllChecked: boolean;
  handleCheckedCloudsites: (cloudSiteId: string) => void;
  handleCheckedOrganisation: (cloudSiteId: string) => void;
  currency?: string;
  handleMatchBillDates?: (lineItemId?: string) => void;
  onAddNewProductsClick?: (cloudSiteId: string, cloudSiteHostName: string) => void;
  isTWCPhase2Enabled?: boolean;
  isDesignIteration1Enabled?: boolean;
  isAppificationEnabled?: boolean;
  handleAddNewAppsClick?: (params: {
    product: any;
    cloudSiteId: string;
    cloudSiteUrl: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds: string[];
  }) => void;
  setHasErrorFetchingOfferings?: (hasError: boolean) => void;
  isMultiCurrencyEnabled?: boolean;
  isAddOnsSearchEnabled?: boolean;
};

export const ProductLineItemsTable = ({
  productLineItems,
  productIdPlanOptionsMap,
  setSelectedPricingPlanId,
  setActiveUsers,
  onRemoveProductFromCart,
  handleCheckedProductLineItems,
  selectiveProductRemovalEnable,
  checkedBillDate,
  matchBillDateEnabled,
  showProductsPromotionView,
  showTotalColumn,
  showTaxEstimationColumn,
  checkedItemMap,
  shouldShowTrialStatus,
  enrichmentStatus,
  isAllowedToEditUsersCount,
  showUnchangedLineItems,
  selectedProductIdsWithoutChanges,
  hideCustomerContactColumn,
  nextBillDate,
  showSandboxProducts,
  isSandboxDecouplingEnabled,
  isNonCommercialPricingEnabled,
  handleCheckedSelectAll,
  isIndeterminate,
  isSelectAllChecked,
  handleCheckedOrganisation,
  handleCheckedCloudsites,
  handleMatchBillDates,
  currency,
  onAddNewProductsClick,
  handleAddNewAppsClick,
  isTWCPhase2Enabled,
  isDesignIteration1Enabled,
  isAppificationEnabled,
  setHasErrorFetchingOfferings,
  isMultiCurrencyEnabled,
  isAddOnsSearchEnabled,
}: Props) => {
  const getHeaders = memoize(() => {
    const headers = [
      {
        id: 'select-all',
        name: CART_COLUMNS_CONSTANTS.SELECT,
        renderComponent: (
          <th>
            <Checkbox
              isIndeterminate={isIndeterminate}
              isChecked={isSelectAllChecked}
              testId="selected-item-checkbox"
              onChange={handleCheckedSelectAll}
              label=""
            />
          </th>
        ),
      },
      {
        id: 'product-name',
        name: isAppificationEnabled ? CART_COLUMNS_CONSTANTS.SUBSCRIPTION : CART_COLUMNS_CONSTANTS.PRODUCT,
        customClassNames: 'col-product-name',
      },
      {
        id: 'product-plan',
        name: CART_COLUMNS_CONSTANTS.PLAN,
        customClassNames: 'col-common-header',
      },
      {
        id: 'product-quantity',
        name: CART_COLUMNS_CONSTANTS.QUANTITY,
        customClassNames: 'col-common-header',
      },
      {
        id: 'product-next-bill-date',
        name: CART_COLUMNS_CONSTANTS.NEXT_BILL_DATE,
        customClassNames: 'col-common-header',
      },
      {
        id: 'product-list-price',
        name: CART_COLUMNS_CONSTANTS.LIST_PRICE,
        renderComponent: (
          <th className="col-common-header">
            <Text>
              {CART_COLUMNS_CONSTANTS.LIST_PRICE}
              {currency && ` (${currency})`}
            </Text>
          </th>
        ),
      },
      {
        id: 'product-other-discount',
        name: CART_COLUMNS_CONSTANTS.OTHER_DISCOUNT,
        renderComponent: (
          <th className="col-common-header">
            <Text>
              {CART_COLUMNS_CONSTANTS.OTHER_DISCOUNT}
              {currency && ` (${currency})`}
            </Text>
          </th>
        ),
      },
      {
        id: 'product-partner-discount',
        name: CART_COLUMNS_CONSTANTS.PARTNER_DISCOUNT,
        renderComponent: (
          <th className="col-common-header">
            <Text>
              {CART_COLUMNS_CONSTANTS.PARTNER_DISCOUNT}
              {currency && ` (${currency})`}
            </Text>
          </th>
        ),
      },
      {
        id: 'product-tax-estimate',
        name: CART_COLUMNS_CONSTANTS.TAX_ESTIMATE,
        renderComponent: (
          <th className="col-common-header">
            <Text>
              {CART_COLUMNS_CONSTANTS.TAX_ESTIMATE}
              {currency && ` (${currency})`}
            </Text>
          </th>
        ),
      },
      {
        id: 'product-total',
        name: CART_COLUMNS_CONSTANTS.TOTAL,
        renderComponent: (
          <th className="col-common-header">
            <Text>
              {CART_COLUMNS_CONSTANTS.TOTAL}
              {currency && ` (${currency})`}
            </Text>
          </th>
        ),
      },
      {
        id: 'product-status',
        name: CART_COLUMNS_CONSTANTS.STATUS,
        renderComponent: (
          <th className="col-common-header">
            <Text>{CART_COLUMNS_CONSTANTS.STATUS}</Text>
          </th>
        ),
      },
      {
        id: 'product-add-new',
        name: 'Add',
        customClassNames: 'col-product-add-new',
      },
    ];

    return headers;
  });

  const sortLineItems = (a: Product | cartLinkedEntitlement, b: Product | cartLinkedEntitlement) => {
    const firstName = defaultTo(a?.name, a?.name ?? '');
    const secondName = defaultTo(b?.name, b?.name ?? '');
    return firstName.localeCompare(secondName);
  };

  const getProductRow = useCallback(
    ({
      product,
      cloudSite,
      linkedEntitlements,
      isExpandableRow,
      isOrgProduct,
      cloudSiteId,
      handleAddNewAppsClick,
    }: {
      product: Product;
      linkedEntitlements?: LinkedEntitlements;
      cloudSite: string;
      cloudSiteId?: string;
      isExpandableRow?: boolean;
      isOrgProduct?: boolean;
      handleAddNewAppsClick?: (params: {
        product: any;
        cloudSiteId: string;
        cloudSiteUrl: string;
        onAddToCart: (OrderItems: CatalogueContextItems) => void;
        sandboxCloudSiteParentProductIds: string[];
      }) => void;
    }) => {
      const isRowHighlighedForReview =
        showUnchangedLineItems &&
        selectedProductIdsWithoutChanges?.length &&
        selectedProductIdsWithoutChanges.includes(product.id);

      const columns = getHeaders().map((header) => header.name);
      return (
        <CartLineItemRowV2
          key={product.id}
          columns={columns}
          product={product}
          linkedEntitlements={linkedEntitlements}
          productIdPlanOptionsMap={productIdPlanOptionsMap}
          enrichmentStatus={enrichmentStatus}
          selectedBillDate={nextBillDate?.cloudSiteLevel?.[cloudSite] || nextBillDate?.cartLevel}
          setSelectedPricingPlanId={setSelectedPricingPlanId}
          setActiveUsers={setActiveUsers}
          onRemoveProductFromCart={onRemoveProductFromCart}
          handleCheckedProductLineItems={handleCheckedProductLineItems}
          handleMatchBillDates={handleMatchBillDates}
          isApp={false}
          showTotalColumn={showTotalColumn}
          showTaxEstimationColumn={showTaxEstimationColumn}
          showProductsPromotionView={showProductsPromotionView}
          checkedItemMap={checkedItemMap}
          selectiveProductRemovalEnable={selectiveProductRemovalEnable}
          matchBillDateEnabled={matchBillDateEnabled}
          checkedBillDate={checkedBillDate}
          shouldShowTrialStatus={shouldShowTrialStatus}
          isAllowedToEditUsersCount={isAllowedToEditUsersCount}
          isRowHighlighedForReview={!!isRowHighlighedForReview}
          hideCustomerContactColumn={hideCustomerContactColumn}
          isOrgProduct={isOrgProduct}
          showSandboxProducts={showSandboxProducts}
          isSandboxDecouplingEnabled={isSandboxDecouplingEnabled}
          isNonCommercialPricingEnabled={isNonCommercialPricingEnabled}
          isExpandableRow={isExpandableRow}
          cloudSiteId={cloudSiteId}
          cloudSite={cloudSite}
          handleAddNewAppsClick={handleAddNewAppsClick}
          isDefaultExpanded={true}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
          isAppificationEnabled={isAppificationEnabled}
          setHasErrorFetchingOfferings={setHasErrorFetchingOfferings}
          isMultiCurrencyEnabled={isMultiCurrencyEnabled}
          isAddOnsSearchEnabled={isAddOnsSearchEnabled}
        />
      );
    },
    [
      productIdPlanOptionsMap,
      enrichmentStatus,
      nextBillDate,
      checkedItemMap,
      selectiveProductRemovalEnable,
      matchBillDateEnabled,
      checkedBillDate,
      shouldShowTrialStatus,
      showTotalColumn,
      showTaxEstimationColumn,
      showProductsPromotionView,
      showProductsPromotionView,
      showUnchangedLineItems,
      selectedProductIdsWithoutChanges,
      hideCustomerContactColumn,
      showSandboxProducts,
      isSandboxDecouplingEnabled,
      isNonCommercialPricingEnabled,
      isAllowedToEditUsersCount,
      handleAddNewAppsClick,
      isTWCPhase2Enabled,
      isDesignIteration1Enabled,
    ]
  );

  const getProductRows = useCallback(
    ({
      lineItem,
      handleAddNewAppsClick,
    }: {
      lineItem: any;
      handleAddNewAppsClick?: (params: {
        product: any;
        cloudSiteId: string;
        cloudSiteUrl: string;
        onAddToCart: (OrderItems: CatalogueContextItems) => void;
        sandboxCloudSiteParentProductIds: string[];
      }) => void;
    }) => {
      const { cloudSite, cloudSiteId, products, isOrgProduct } = lineItem;
      // `productRows` will contain list of products as a table row
      const productRows: React.ReactNode[] = [];
      const jiraproductRows: React.ReactNode[] = [];
      const sortedProducts = (products as StandaloneProductWithApps[])?.sort(sortLineItems);

      /**
       * Building table rows based on the list of products a user selected.
       * A product can be standalone or part of a container. Same is decided based on the value of `isContainer` property.
       * If product is standalone, we can directly render the row and then render the apps (apps render pending implementation).
       * If product is part of a container, we need to render it along with all of it's siblings together and then render the apps (apps render pending implementation).
       */
      (sortedProducts as PresentationLayerProducts).forEach((product) => {
        const isJiraContainer = product.type === 'container';
        const linkedEntitlements = product.linkedEntitlements;
        if (!isJiraContainer) {
          productRows.push(
            getProductRow({
              product,
              cloudSite,
              isOrgProduct,
              linkedEntitlements,
              isExpandableRow: linkedEntitlements?.length > 0,
              cloudSiteId,
              handleAddNewAppsClick,
            })
          );
        } else {
          const sortedProducts = product.products?.sort(sortLineItems);
          const lastIndex = sortedProducts.length - 1;
          sortedProducts.forEach((jiraProduct, index) => {
            if (index === lastIndex) {
              jiraproductRows.push(
                getProductRow({
                  product: jiraProduct,
                  cloudSite,
                  isOrgProduct,
                  linkedEntitlements,
                  isExpandableRow: linkedEntitlements?.length > 0,
                  cloudSiteId,
                  handleAddNewAppsClick,
                })
              );
            } else {
              jiraproductRows.push(
                getProductRow({ product: jiraProduct, cloudSite, isOrgProduct, cloudSiteId, handleAddNewAppsClick })
              );
            }
          });
        }
      });

      return [...productRows, ...jiraproductRows];
    },
    [getProductRow]
  );

  const cloudSiteRows = useMemo(() => {
    return productLineItems?.map((lineItem: any) => {
      const { cloudSite, cloudSiteId, orgName } = lineItem;

      const isOrgNamePresent = !isEmpty(orgName);

      const headers = getHeaders();
      const addButtonComponent = !isOrgNamePresent ? (
        <Tooltip content={isAppificationEnabled ? 'New subscription' : 'New product'} position="bottom">
          <Button
            className="add-new-product-button"
            testId={`add-new-product-button-${cloudSiteId || NEW_CLOUD_SITE_ID}`}
            iconBefore={<AddIcon label="add-product" size="small" />}
            onClick={() => onAddNewProductsClick?.(cloudSiteId || NEW_CLOUD_SITE_ID, cloudSite || NEW_CLOUD_SITE)}
            appearance="default"
          />
        </Tooltip>
      ) : null;

      const onCheckboxToggle = () => {
        isOrgNamePresent ? handleCheckedOrganisation(orgName) : handleCheckedCloudsites(cloudSite);
      };

      const cloudSiteOrOrgRow = getCloudSiteOrgRow({
        totalColumns: headers.length,
        orgName: orgName,
        cloudSiteName: cloudSite,
        isChecked: isOrgNamePresent ? !!checkedItemMap[orgName] : !!checkedItemMap[cloudSite],
        onCheckboxToggle: onCheckboxToggle,
        addButtonComponent,
      });

      // this is required to open the cloud site row if any product is not changed
      const hasProductWithoutChanges = lineItem?.products?.some((product: any) => {
        const isJiraContainer = product.type === 'container';
        if (!isJiraContainer) return selectedProductIdsWithoutChanges?.includes(product.id);
        else {
          return product.products.some((jiraProduct: any) =>
            selectedProductIdsWithoutChanges?.includes(jiraProduct.id)
          );
        }
      });

      return (
        <ExpandableRow
          key={`cloud-site-cell-${cloudSiteId}`}
          testId={`cloud-site-row-${cloudSiteId ? cloudSiteId : 'new'}`}
          className="cloud-site-row"
          rowsAsCells={cloudSiteOrOrgRow}
          subRows={getProductRows({ lineItem, handleAddNewAppsClick })}
          expandableRowColumn={1}
          isDefaultExpanded={true}
          isCustomExpanded={showUnchangedLineItems && hasProductWithoutChanges}
        />
      );
    });
  }, [
    productLineItems,
    checkedItemMap,
    handleCheckedOrganisation,
    handleCheckedCloudsites,
    showUnchangedLineItems,
    selectedProductIdsWithoutChanges,
  ]);

  return <CartLineItemsTable headerConfig={getHeaders()} bodyComponent={cloudSiteRows} />;
};
